import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { projectsApi } from '../api/projects';
import { seriesOffersApi } from '../api/seriesOffers';
import { seriesSnapshotsApi } from '../api/seriesSnapshots';
import { BOOK_LINE_LABEL } from '../utils/seriesSharedBooks';
import {
  CHECKLIST_LEDE,
  FROM_TALKS_AND_NOTES,
  KIND_LABEL,
  REVIEW_GROUP_ORDER,
  REVIEW_LEDE,
  THIS_BOOK_FLAG,
  destinationNote,
  groupCandidates,
  loadMakeCardsSources,
  looksLikeThisBook,
  persistAcceptedBookIdea,
  selectedIds,
  snapshotCardFromCandidate,
  visibleCandidates,
} from '../utils/seriesOffers';
import { seriesCardsPath, upsertCard } from '../utils/seriesSnapshots';
import '../components/BookSheet/BookSheet.css';
import './SeriesOffersPage.css';

/**
 * Writer checklist of series talks and note files (#678).
 * One pass. Propose, review, accept or skip. People, place, and plot
 * snapshots show even when no book is named. Book accept writes one
 * Timeline idea card only when the title is a real book name. Never starts a book.
 */
export default function SeriesOffersPage() {
  const { projectId } = useParams();
  const { currentProject, setCurrentProject, setCurrentStory } = useApp();

  const [project, setProject] = useState(
    currentProject?.id === projectId ? currentProject : null,
  );
  const [talks, setTalks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [checked, setChecked] = useState({});
  const [step, setStep] = useState('checklist');
  const [candidates, setCandidates] = useState([]);
  const [scopeNotes, setScopeNotes] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [done, setDone] = useState({});
  const [loading, setLoading] = useState(true);
  const [proposing, setProposing] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setCurrentStory(null);
  }, [setCurrentStory]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!projectId) return;
      if (currentProject?.id === projectId) {
        setProject(currentProject);
        return;
      }
      try {
        const next = await projectsApi.get(projectId);
        if (!cancelled && next) {
          setProject(next);
          setCurrentProject(next);
        }
      } catch {
        if (!cancelled) setProject(null);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [projectId, currentProject, setCurrentProject]);

  const loadSources = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const checklist = await loadMakeCardsSources(
        projectId,
        project?.discovery_session_id,
      );
      setTalks(checklist.talks);
      setNotes(checklist.notes);
      const nextChecked = {};
      [...checklist.talks, ...checklist.notes].forEach((item) => {
        nextChecked[item.id] = true;
      });
      setChecked(nextChecked);
      setError(checklist.error ? (checklist.error.message || 'Could not load chats and notes.') : null);
    } catch (err) {
      setError(err.message || 'Could not load chats and notes.');
    } finally {
      setLoading(false);
    }
  }, [projectId, project?.discovery_session_id]);

  useEffect(() => { loadSources(); }, [loadSources]);

  const sessionIds = useMemo(() => selectedIds(checked, talks), [checked, talks]);
  const batchIds = useMemo(() => selectedIds(checked, notes), [checked, notes]);
  const groups = useMemo(() => groupCandidates(candidates), [candidates]);
  const remaining = candidates.filter((row) => !done[row.id]);

  const toggle = (id) => {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const propose = async () => {
    if (!sessionIds.length && !batchIds.length) {
      setError('Pick at least one series chat or note file.');
      return;
    }
    setProposing(true);
    try {
      const result = await seriesOffersApi.propose(projectId, { sessionIds, batchIds });
      const next = visibleCandidates(result?.candidates || []);
      setCandidates(next);
      setScopeNotes(result?.scope_notes || []);
      const nextDrafts = {};
      next.forEach((row) => {
        nextDrafts[row.id] = { title: row.title, statement: row.statement };
      });
      setDrafts(nextDrafts);
      setDone({});
      setStep('review');
      setError(null);
    } catch (err) {
      setError(err.message || 'Could not propose notes from those sources.');
    } finally {
      setProposing(false);
    }
  };

  const editDraft = (id, field, value) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const skip = (id) => {
    setDone((prev) => ({ ...prev, [id]: 'skipped' }));
  };

  const accept = async (candidate) => {
    const draft = drafts[candidate.id] || candidate;
    const nextCandidate = {
      ...candidate,
      title: (draft.title || '').trim(),
      statement: (draft.statement || '').trim(),
    };
    if (!nextCandidate.title || !nextCandidate.statement) {
      setError('Give this offer a name and a line before you accept it.');
      return;
    }
    setSavingId(candidate.id);
    try {
      if (candidate.kind === 'book') {
        await persistAcceptedBookIdea(projectId, nextCandidate);
      } else {
        const store = await seriesSnapshotsApi.get(projectId);
        const next = upsertCard(store, snapshotCardFromCandidate(nextCandidate));
        await seriesSnapshotsApi.put(projectId, next);
      }
      setDone((prev) => ({ ...prev, [candidate.id]: 'accepted' }));
      setError(null);
    } catch (err) {
      setError(err.message || 'Could not accept this offer.');
    } finally {
      setSavingId(null);
    }
  };

  const renderSourceList = (items, heading) => (
    <section className="so-section">
      <h2>{heading}</h2>
      {items.length === 0 && <p className="so-empty">None in this project yet.</p>}
      <ul className="so-list">
        {items.map((item) => (
          <li key={item.id}>
            <label className="so-check">
              <input
                type="checkbox"
                checked={Boolean(checked[item.id])}
                onChange={() => toggle(item.id)}
              />
              <span>
                <strong>{item.label}</strong>
                <em>{item.detail}</em>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </section>
  );

  const renderCandidate = (candidate) => {
    const draft = drafts[candidate.id] || candidate;
    const status = done[candidate.id];
    if (status) {
      return (
        <li key={candidate.id} className="so-card so-card--done">
          <p>{status === 'accepted' ? 'Accepted.' : 'Skipped.'} {candidate.title}</p>
        </li>
      );
    }
    return (
      <li key={candidate.id} className="so-card">
        <label className="so-field">
          <span>{candidate.kind === 'book' ? 'Title' : 'Name'}</span>
          <input
            value={draft.title}
            onChange={(e) => editDraft(candidate.id, 'title', e.target.value)}
          />
        </label>
        <label className="so-field">
          <span>{candidate.kind === 'book' ? BOOK_LINE_LABEL : 'Snapshot'}</span>
          <textarea
            rows={4}
            value={draft.statement}
            onChange={(e) => editDraft(candidate.id, 'statement', e.target.value)}
          />
        </label>
        <p className="so-dest">{destinationNote(candidate)}</p>
        {looksLikeThisBook(candidate) && <p className="so-flag">{THIS_BOOK_FLAG}</p>}
        {candidate.existing?.length > 0 && (
          <div className="so-existing">
            <p>Already on the board — reject this if it is a duplicate. Nothing merges on its own.</p>
            <ul>
              {candidate.existing.map((row) => (
                <li key={`${row.store}-${row.id}`}>
                  {row.name}: {row.description || 'No line yet.'}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="so-actions">
          <button
            type="button"
            className="bs-btn bs-btn--primary bs-btn--small"
            onClick={() => accept(candidate)}
            disabled={savingId === candidate.id}
          >
            Accept
          </button>
          <button
            type="button"
            className="bs-btn bs-btn--secondary bs-btn--small"
            onClick={() => skip(candidate.id)}
            disabled={savingId === candidate.id}
          >
            Skip
          </button>
        </div>
      </li>
    );
  };

  if (loading) return <div className="bs-panel"><p>Loading…</p></div>;

  return (
    <div className="bs-panel so-page">
      <div className="bs-header">
        <div>
          <h1>{FROM_TALKS_AND_NOTES}</h1>
          <p className="bs-header__subtitle">{project?.name || ''}</p>
        </div>
        <Link className="bs-add-link" to={seriesCardsPath(projectId)}>Back to Series Notes</Link>
      </div>

      {error && (
        <div className="bs-error">
          {error}{' '}
          <button type="button" className="bs-btn--danger" onClick={() => setError(null)}>dismiss</button>
        </div>
      )}

      {step === 'checklist' && (
        <>
          <p className="so-lede">{CHECKLIST_LEDE}</p>
          {renderSourceList(talks, 'Series chats')}
          {renderSourceList(notes, 'Uploaded files')}
          <button
            type="button"
            className="bs-btn bs-btn--primary"
            onClick={propose}
            disabled={proposing}
          >
            {proposing ? 'Proposing…' : 'Make notes from these'}
          </button>
        </>
      )}

      {step === 'review' && (
        <>
          <p className="so-lede">{REVIEW_LEDE}</p>
          {scopeNotes.some((note) => note.scope_note) && (
            <p className="so-scope">
              Digest notes stay as notes.
              {scopeNotes.filter((note) => note.scope_note).map((note) => ` ${note.scope_note}`).join('')}
            </p>
          )}
          {remaining.length === 0 && (
            <p className="so-lede">
              This draft is finished.
              {' '}
              <Link to={`/projects/${projectId}/series`}>Open Timeline</Link>
              {' · '}
              <Link to={seriesCardsPath(projectId)}>Open Series Notes</Link>
            </p>
          )}
          {REVIEW_GROUP_ORDER.map((kind) => (
            groups[kind].length > 0 && (
              <section key={kind} className="so-section">
                <h2>{KIND_LABEL[kind]}</h2>
                <ul className="so-cards">{groups[kind].map(renderCandidate)}</ul>
              </section>
            )
          ))}
          <button type="button" className="bs-btn bs-btn--secondary" onClick={() => setStep('checklist')}>
            Back to checklist
          </button>
        </>
      )}
    </div>
  );
}
